import React, { useState } from 'react';
import { Box, Typography, Stack, Menu, MenuItem } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

// Define the custom color constants based on the design (Green and Grey)
const GREEN_COLOR = '#008060'; // Adjusted a standard green to match the Figma preview
const GREY_FONT_COLOR = '#828584'; // The 'Grey font' color from the sidebar

interface TimePeriodSelectorProps {
  /** The currently selected time period text (e.g., "This month") */
  selectedPeriod: string;
  /** Callback function when the selector is clicked */
  onClick?: () => void;
  /** Periods to choose from; when given, clicking opens a menu */
  options?: string[];
  /** Called with the chosen period */
  onChange?: (period: string) => void;
}

export const TimePeriodSelector: React.FC<TimePeriodSelectorProps> = ({ selectedPeriod, onClick, options, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const hasMenu = !!options?.length;

  return (
    <>
    <Box
      role={hasMenu ? 'button' : undefined}
      tabIndex={hasMenu ? 0 : undefined}
      aria-haspopup={hasMenu ? 'menu' : undefined}
      aria-expanded={hasMenu ? !!anchor : undefined}
      aria-label={hasMenu ? `Time period: ${selectedPeriod}` : undefined}
      onKeyDown={hasMenu ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAnchor(e.currentTarget); } } : undefined}
      // The main container for the pill shape and border
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '8px 20px', // Adjust padding for vertical and horizontal spacing
        borderRadius: '50px', // High border radius for the pill shape
        border: `2px solid ${GREEN_COLOR}`,
        cursor: 'pointer',
        transition: 'background-color 0.3s',
        '&:hover': {
          backgroundColor: 'rgba(0, 128, 96, 0.05)', // Light green hover effect
        },
      }}
      onClick={(e) => {
        if (hasMenu) setAnchor(e.currentTarget);
        onClick?.();
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {/* Calendar Icon */}
        <CalendarMonthIcon sx={{ fontSize: "1rem", color: GREEN_COLOR }} />

        {/* Text Label */}
        <Typography
          variant="h5" // Use a larger variant for prominence
          sx={{
            fontFamily: 'Lexend, sans-serif', // Using 'Lexend' if available, otherwise fallback
            fontWeight: 400,
            fontSize: '0.89rem', // Larger font size for the main text
            color: "#022219",
            lineHeight: 1, // Ensure text is centered vertically
          }}
        >
          {selectedPeriod}
        </Typography>

        {/* Dropdown Arrow */}
        <KeyboardArrowDownIcon sx={{ fontSize: "1rem", color: GREY_FONT_COLOR }} />
      </Stack>
    </Box>
    {hasMenu && (
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {options!.map((option) => (
          <MenuItem
            key={option}
            selected={option === selectedPeriod}
            onClick={() => {
              setAnchor(null);
              onChange?.(option);
            }}
          >
            {option}
          </MenuItem>
        ))}
      </Menu>
    )}
    </>
  );
};